import { useMemo, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import AdminLogin from './pages/AdminLogin';
import Overview from './pages/Overview';
import MacroCalendar from './pages/MacroCalendar';
import StrategyOps from './pages/StrategyOps';
import RuntimeMonitor from './pages/RuntimeMonitor';
import Users from './pages/Users';
import Subscriptions from './pages/Subscriptions';
import TradingAccounts from './pages/TradingAccounts';
import AuditLogs from './pages/AuditLogs';
import Settings from './pages/Settings';
import AiUsage from './pages/AiUsage';
import AiPolicies from './pages/AiPolicies';
import AiEvents from './pages/AiEvents';
import Mt5Activations from './pages/Mt5Activations';
import AdminSignals from './pages/AdminSignals';
import CopyTrading from './pages/CopyTrading';
import { AdminUser, getMe, logout } from './lib_api';
import 'react-toastify/dist/ReactToastify.css';

const titles: Record<string, { title: string; subtitle: string }> = {
  '/': { title: 'Overview', subtitle: 'Internal operations snapshot for Pickaxe.' },
  '/macro-calendar': { title: 'Macro Calendar', subtitle: 'Primary backend-managed calendar for critical USD events.' },
  '/strategy-ops': { title: 'Strategy Ops', subtitle: 'Visibility into strategy runtime and selection state.' },
  '/runtime-monitor': { title: 'Trading Health', subtitle: 'Live checks for AI and Manual evaluation engines.' },
  '/users': { title: 'Users', subtitle: 'Every registered Pickaxe user and current access state.' },
  '/subscriptions': { title: 'Subscriptions', subtitle: 'Activate, update, or disable Gold and Platinum access.' },
  '/trading-accounts': { title: 'Trading Accounts', subtitle: 'Live cross-user MT5 connection and Algo status.' },
  '/audit-logs': { title: 'Audit Logs', subtitle: 'Admin/system activity trail scaffold.' },
  '/settings': { title: 'Settings', subtitle: 'Admin console configuration.' },
  '/ai-usage': { title: 'AI Usage', subtitle: 'Cost and usage controls for AI gateway.' },
  '/ai-policies': { title: 'AI Policies', subtitle: 'Feature-level AI policy controls and toggles.' },
  '/ai-events': { title: 'AI Events', subtitle: 'Recent AI request events and blocked reasons.' },
  '/mt5-activations': { title: 'MT5 Activations', subtitle: 'Manual AutoTrading activation queue for managed terminals.' },
  '/admin-signals': { title: 'Admin Signals', subtitle: 'Analyze charts and forward reviewed trade recommendations to users.' },
  '/copy-trading': { title: 'Copy Trading', subtitle: 'Control the approved provider and monitor subscribed users.' },
  '/backtests': { title: 'Backtests', subtitle: 'Backtesting moved to Strategy Ops → Backtest Lab.' },
};

function ProtectedLayout() {
  const loc = useLocation();
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const me = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: getMe,
    retry: false,
  });

  const current = useMemo(() => titles[loc.pathname] || { title: 'Pickaxe Admin', subtitle: 'Internal operations dashboard.' }, [loc.pathname]);

  if (me.isLoading) return <div className="min-h-screen grid place-items-center text-sm">Loading admin session…</div>;
  if (me.isError || !me.data || !['admin', 'superadmin'].includes(String(me.data.role))) return <Navigate to="/login" replace />;

  return (
    <div className="app-body min-h-screen">
      <ToastContainer />
      <div className="flex min-h-screen">
        <div className="hidden lg:block">
          <Sidebar />
        </div>
        {mobileNavOpen ? (
          <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
            <button
              type="button"
              className="absolute inset-0 bg-black/40"
              aria-label="Close navigation"
              onClick={() => setMobileNavOpen(false)}
            />
            <div className="relative h-full w-72 max-w-[85vw]">
              <Sidebar onClose={() => setMobileNavOpen(false)} />
            </div>
          </div>
        ) : null}
        <div className="flex-1">
          <Navbar
            title={current.title}
            subtitle={current.subtitle}
            onOpenMobileNav={() => setMobileNavOpen(true)}
            onLogout={async () => {
              await logout();
              queryClient.clear();
              nav('/login', { replace: true });
            }}
          />
          <main className="p-3 sm:p-4 lg:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}

function LoginRoute({ onLoggedIn }: { onLoggedIn: (user: AdminUser) => Promise<void> | void }) {
  const me = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: getMe,
    retry: false,
  });

  if (me.isSuccess && me.data && ['admin', 'superadmin'].includes(String(me.data.role))) {
    if (import.meta.env.DEV) {
      console.info('[admin-auth] already authenticated, redirecting from /login to /');
    }
    return <Navigate to="/" replace />;
  }

  return <AdminLogin onLoggedIn={onLoggedIn} />;
}

export default function App() {
  const qc = useQueryClient();

  return (
    <Routes>
      <Route
        path="/login"
        element={(
          <LoginRoute
            onLoggedIn={async (user) => {
              qc.setQueryData(['admin', 'me'], user);
              await qc.invalidateQueries({ queryKey: ['admin', 'me'] });
            }}
          />
        )}
      />
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<Overview />} />
        <Route path="/macro-calendar" element={<MacroCalendar />} />
        <Route path="/strategy-ops" element={<StrategyOps />} />
        <Route path="/runtime-monitor" element={<RuntimeMonitor />} />
        <Route path="/users" element={<Users />} />
        <Route path="/subscriptions" element={<Subscriptions />} />
        <Route path="/trading-accounts" element={<TradingAccounts />} />
        <Route path="/audit-logs" element={<AuditLogs />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/ai-usage" element={<AiUsage />} />
        <Route path="/ai-policies" element={<AiPolicies />} />
        <Route path="/ai-events" element={<AiEvents />} />
        <Route path="/mt5-activations" element={<Mt5Activations />} />
        <Route path="/admin-signals" element={<AdminSignals />} />
        <Route path="/copy-trading" element={<CopyTrading />} />
        <Route path="/backtests" element={<Navigate to="/strategy-ops" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
